import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Mail } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable "Send by email" button. Attaches any PDF URL to the outgoing email.
 * Uses the org's Resend integration configured in Settings → E-Invoice / EWB.
 *
 * Props: reportUrl, reportLabel, defaultRecipient, defaultSubject
 */
export default function SendReportButton({ reportUrl, reportLabel = "Report",
                                              defaultRecipient = "", defaultSubject = "" }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [cc, setCC] = useState("");
  const [subject, setSubject] = useState(defaultSubject || `${reportLabel} from Zenith 360`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!recipient.includes("@")) return toast.error("Enter a valid recipient email");
    setBusy(true);
    try {
      const r = await api.post("/email/send-report", {
        recipient, subject,
        cc: cc ? cc.split(",").map(s => s.trim()).filter(s => s.includes("@")) : null,
        message, report_url: reportUrl, report_label: reportLabel,
      });
      if (r.data.ok) {
        toast.success(`✓ Sent · id ${r.data.provider_id?.slice(0, 12) || "queued"}${r.data.attached ? " · PDF attached" : ""}`);
        setOpen(false);
      } else {
        toast.error(`Send failed: ${r.data.error || "unknown"}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-sm gap-1.5" data-testid="send-email-btn">
          <Mail className="w-3.5 h-3.5" /> Send by email
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="send-email-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Send {reportLabel}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>To *</Label>
            <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="rounded-sm mt-1" placeholder="cfo@company.in" data-testid="send-to" />
          </div>
          <div><Label>Cc (comma-separated)</Label>
            <Input value={cc} onChange={(e) => setCC(e.target.value)} className="rounded-sm mt-1" placeholder="auditor@ca.in, controller@company.in" data-testid="send-cc" />
          </div>
          <div><Label>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="rounded-sm mt-1" data-testid="send-subject" />
          </div>
          <div><Label>Message (optional)</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-sm mt-1 min-h-[80px]" placeholder="Any notes for the recipient…" data-testid="send-message" />
          </div>
          {reportUrl && (
            <div className="text-[10px] text-muted-foreground italic">📎 Will attach: <span className="font-mono">{reportLabel}.pdf</span></div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={send} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="send-confirm">
              {busy ? "Sending…" : "Send now"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
